"use client";

import { useEffect, useRef, Suspense } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import posthog from "posthog-js";
import { isPostHogSuppressedPath } from "@/lib/posthog-suppressed-path";

/**
 * Client-side pageview tracker that runs AFTER React hydration.
 * Avoids the hydration-mismatch bug from PostHog's built-in capture_pageview:"history_change"
 * (see instrumentation-client.ts comment). Mount inside a Suspense boundary in the locale layout.
 */
function PageviewCapture() {
	const pathname = usePathname();
	const searchParams = useSearchParams();
	const lastUrl = useRef<string | null>(null);

	useEffect(() => {
		if (typeof window === "undefined" || !pathname) return;
		if (isPostHogSuppressedPath(pathname)) {
			lastUrl.current = null;
			return;
		}
		// Only fire when PostHog actually initialized (instrumentation-client gates on key + disable flag)
		if (!posthog.__loaded || posthog.has_opted_out_capturing()) return;

		const query = searchParams?.toString();
		const url = query ? `${pathname}?${query}` : pathname;

		if (lastUrl.current === url) return;
		lastUrl.current = url;
		posthog.set_config({ autocapture: true });

		posthog.capture("$pageview", {
			$current_url: window.location.origin + url,
			$pathname: pathname,
		});
	}, [pathname, searchParams]);

	return null;
}

export function PostHogPageviewTracker() {
	return (
		<Suspense fallback={null}>
			<PageviewCapture />
		</Suspense>
	);
}
